import {XMarkIcon} from "@heroicons/react/20/solid";
import type {ReactNode} from "react";
import {OtsIconActionButton} from "../../components/OtsButton";
import {DescriptionList} from "../../components/DescriptionList";
import {OtsThemeRoot} from "../../theme/OtsTheme";

export type MapInfoPanelProps = {
  title: ReactNode;
  /** Names the panel for assistive tech when the title is a link or carries markup. */
  ariaLabel: string;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Status badges under the title. */
  badges?: ReactNode;
  /** Buttons beside the title, before close. */
  actions?: ReactNode;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
};

const heading = "!m-0 text-[11px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]";

/**
 * The card the live map opens beside a clicked site, unit or ops status: a header that stays put and sections
 * that scroll beneath it, as tall as the map allows.
 */
export const MapInfoPanel = ({title, ariaLabel, subtitle, icon, badges, actions, onClose, children, className}: MapInfoPanelProps) => (
  <OtsThemeRoot
    role="region"
    aria-label={ariaLabel}
    className={[
      "!flex max-h-full w-[375px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] text-[var(--ots-text)] shadow-lg",
      className,
    ].filter(Boolean).join(" ")}>
    <header className="!flex shrink-0 flex-col gap-2 border-b border-[var(--ots-outline-variant)] px-4 pb-3 pt-3.5">
      <div className="flex items-start gap-2">
        {icon && <span aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-[var(--ots-primary)] [&>svg]:size-full">{icon}</span>}
        <div className="min-w-0 flex-1">
          <h2 className="!m-0 break-words text-base font-semibold leading-6 text-[var(--ots-text)]">{title}</h2>
          {subtitle && <p className="!m-0 truncate text-xs text-[var(--ots-text-muted)]">{subtitle}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
        {onClose && (
          <OtsIconActionButton variant="text" size="mini" className="!overflow-hidden" label="Close" icon={<XMarkIcon/>} onClick={onClose}/>
        )}
      </div>
      {badges && <div className="flex flex-wrap gap-1.5">{badges}</div>}
    </header>
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-4 py-4">
      {children}
    </div>
  </OtsThemeRoot>
);

export type MapInfoSectionProps = {
  title: string;
  /** Beside the title: a status badge, when it was updated. */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
};

/** A titled part of a map panel, ruled off from the one above. */
export const MapInfoSection = ({title, aside, children, className}: MapInfoSectionProps) => (
  <section
    aria-label={title}
    className={["flex flex-col gap-3 border-t border-[var(--ots-outline-variant)] pt-4 first:border-t-0 first:pt-0", className].filter(Boolean).join(" ")}>
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <h3 className={heading}>{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

/** Two columns of label-over-value facts, sized for the panel rather than the window. */
export const MapInfoFields = ({children}: {children: ReactNode}) => (
  <DescriptionList compact className="!grid-cols-2 !gap-y-3">{children}</DescriptionList>
);
